import { PlatformLocation } from '@angular/common';
import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { Product, ProductCollection } from '../models/product';

interface SeoOptions {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: string;
  robots?: string;
  keywords?: string;
}

const SITE_DESCRIPTION =
  "Discover Pink Soul, your destination for chic and trendy women's fashion. Shop stylish dresses, tops, sweaters, and accessories with free shipping on orders over $99.";

const DEFAULT_IMAGE = '/images/home/banner.webp';
const DEFAULT_KEYWORDS =
  "Pink Soul, women's fashion, trendy clothing, dresses, tops, sweaters, boutique fashion, women's boutique, online fashion store";
const INDEX_ROBOTS = 'index, follow, max-image-preview:large';
const NOINDEX_ROBOTS = 'noindex, nofollow';

const ROUTE_SEO: Record<string, SeoOptions> = {
  '/': {
    description: SITE_DESCRIPTION,
    keywords: DEFAULT_KEYWORDS,
    image: DEFAULT_IMAGE,
  },
  '/about-us': {
    title: 'About Us',
    description:
      "Learn about Pink Soul, a women's fashion boutique focused on chic, trend-forward styles for everyday confidence.",
    keywords:
      "about Pink Soul, women's boutique, fashion boutique, trendy women's clothing",
  },
  '/contact-us': {
    title: 'Contact Us',
    description:
      'Contact Pink Soul customer support for order questions, account help, returns, exchanges, and general inquiries.',
    keywords: 'Pink Soul contact, customer support, order help, returns support',
  },
  '/faq': {
    title: 'Frequently Asked Questions',
    description:
      'Find answers about Pink Soul orders, payments, shipping, returns, exchanges, accounts, privacy, and customer support.',
    keywords:
      'Pink Soul FAQ, shipping questions, return policy, exchange policy, payment methods',
  },
  '/privacy-policy': {
    title: 'Privacy Policy',
    description:
      'Read the Pink Soul privacy policy to learn how customer information is collected, used, and protected.',
  },
  '/terms-of-service': {
    title: 'Terms of Service',
    description:
      'Review the Pink Soul terms of service for shopping, account, payment, and website use guidelines.',
  },
  '/return-and-exchange-policy': {
    title: 'Return & Exchange Policy',
    description:
      'Review Pink Soul return and exchange guidelines, eligibility, timelines, and support contact information.',
    keywords: 'Pink Soul returns, exchange policy, refund policy, return shipping',
  },
  '/search': {
    title: 'Search',
    description:
      "Search Pink Soul for women's dresses, tops, sweaters, and boutique fashion styles.",
    robots: 'noindex, follow',
  },
  '/shopping/cart': {
    title: 'Shopping Cart',
    description: 'Review your Pink Soul shopping cart before checkout.',
    robots: NOINDEX_ROBOTS,
  },
  '/checkout/result': {
    title: 'Payment Confirmation',
    description: 'View your Pink Soul payment and checkout status.',
    robots: NOINDEX_ROBOTS,
  },
  '/login': {
    title: 'Login',
    description: 'Log in to your Pink Soul account.',
    robots: NOINDEX_ROBOTS,
  },
  '/signup': {
    title: 'Signup',
    description: 'Create a Pink Soul account.',
    robots: NOINDEX_ROBOTS,
  },
  '/signup/sent': {
    title: 'Please verify your email',
    description: 'Check your inbox to verify your Pink Soul account email.',
    robots: NOINDEX_ROBOTS,
  },
  '/signup/verification': {
    title: 'Verifying your email address',
    description: 'Verify your Pink Soul account email address.',
    robots: NOINDEX_ROBOTS,
  },
  '/reset-password': {
    title: 'Reset Password',
    description: 'Reset your Pink Soul account password.',
    robots: NOINDEX_ROBOTS,
  },
  '/account': {
    title: 'Account Profile',
    description: 'Manage your Pink Soul account.',
    robots: NOINDEX_ROBOTS,
  },
  '/account/profile': {
    title: 'Account Profile',
    description: 'Manage your Pink Soul account profile.',
    robots: NOINDEX_ROBOTS,
  },
  '/account/orders': {
    title: 'My Orders',
    description: 'View your Pink Soul order history.',
    robots: NOINDEX_ROBOTS,
  },
};

@Injectable({
  providedIn: 'root',
})
export class TitleService {
  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private dom: Document,
    private platformLocation: PlatformLocation
  ) {}

  initGlobalStructuredData() {
    this.setStructuredData('global-schema', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${environment.host}/#organization`,
          name: environment.appname,
          url: environment.host,
          logo: this.absoluteUrl('/images/logo.png'),
          email: 'support@pinksoul.us',
        },
        {
          '@type': 'WebSite',
          '@id': `${environment.host}/#website`,
          url: environment.host,
          name: environment.appname,
          description: SITE_DESCRIPTION,
          publisher: {
            '@id': `${environment.host}/#organization`,
          },
          potentialAction: {
            '@type': 'SearchAction',
            target: `${environment.host}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'OnlineStore',
          '@id': `${environment.host}/#store`,
          name: environment.appname,
          url: environment.host,
          image: this.absoluteUrl(DEFAULT_IMAGE),
          email: 'support@pinksoul.us',
          parentOrganization: {
            '@id': `${environment.host}/#organization`,
          },
          currenciesAccepted: 'USD',
          paymentAccepted: 'Credit Card',
        },
      ],
    });
  }

  setCanonicalURL(path?: string) {
    const baseUrl = environment.host;
    // Remove existing canonical tag if it exists
    const existingCanonical = this.dom.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }

    // Create or update canonical URL
    const canonicalUrl = path
      ? `${baseUrl}${path}`
      : `${baseUrl}${this.platformLocation.pathname}`;

    // Create new canonical link element
    const link: HTMLLinkElement = this.dom.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', canonicalUrl);

    // Append to head
    this.dom.head.appendChild(link);
  }

  setRouteSeo(path: string) {
    const staticSeo = ROUTE_SEO[path];

    if (staticSeo) {
      this.setSeo({
        ...staticSeo,
        path,
      });
      this.setPageStructuredData(staticSeo.title, staticSeo.description, path);
      return;
    }

    if (path.startsWith('/collection/')) {
      this.setSeo({
        description:
          "Shop curated Pink Soul women's fashion collections, including new arrivals, best sellers, seasonal styles, and boutique favorites.",
        path,
      });
      this.clearStructuredData('page-schema');
      return;
    }

    if (path.startsWith('/product/')) {
      this.setSeo({
        description: SITE_DESCRIPTION,
        path,
      });
      this.clearStructuredData('page-schema');
      return;
    }

    if (path.startsWith('/checkout/')) {
      this.setSeo({
        title: 'Checkout',
        description: 'Complete your Pink Soul order securely.',
        path,
        robots: NOINDEX_ROBOTS,
      });
      this.clearStructuredData('page-schema');
      return;
    }

    this.setSeo({
      description: SITE_DESCRIPTION,
      path,
    });
    this.clearStructuredData('page-schema');
  }

  setTitle(title?: string) {
    const rawTitle = title;
    if (!title) {
      title = `${environment.appname} - Chic & Trendy Women's Fashion`;
    } else {
      title = `${title} - ${environment.appname}`;
    }
    this.title.setTitle(title);
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({
      name: 'application-name',
      content: environment.appname,
    });

    if (!rawTitle) {
      this.meta.updateTag({ name: 'description', content: SITE_DESCRIPTION });
      this.meta.updateTag({ property: 'og:description', content: SITE_DESCRIPTION });
      this.meta.updateTag({ name: 'twitter:description', content: SITE_DESCRIPTION });
    }
  }

  setSeo(options: SeoOptions) {
    if (options.title !== undefined) {
      this.setTitle(options.title);
    }

    const pageTitle = this.title.getTitle();
    const description = options.description ?? SITE_DESCRIPTION;
    const path = options.path ?? this.platformLocation.pathname;
    const image = this.absoluteUrl(options.image ?? DEFAULT_IMAGE);
    const url = this.absoluteUrl(path);

    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'robots',
      content: options.robots ?? INDEX_ROBOTS,
    });
    this.meta.updateTag({
      name: 'googlebot',
      content: options.robots ?? INDEX_ROBOTS,
    });
    this.meta.updateTag({
      name: 'keywords',
      content: options.keywords ?? DEFAULT_KEYWORDS,
    });
    this.meta.updateTag({ property: 'og:site_name', content: environment.appname });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: options.type ?? 'website' });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:alt', content: environment.appname });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
  }

  setCollectionSeo(collection: ProductCollection) {
    const path = `/collection/${collection.no}`;
    const description =
      this.cleanText(collection.description) ||
      `Shop the ${collection.name} collection at ${environment.appname}, featuring chic women's boutique styles curated for fresh outfits and everyday confidence.`;
    const image = collection.cover || DEFAULT_IMAGE;

    this.setSeo({
      title: collection.name,
      description,
      path,
      image,
      type: 'website',
      keywords: `${collection.name}, Pink Soul collection, women's fashion collection, boutique clothing`,
    });
    this.setStructuredData('page-schema', {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${collection.name} - ${environment.appname}`,
      description,
      url: this.absoluteUrl(path),
      image: this.absoluteUrl(image),
      isPartOf: {
        '@id': `${environment.host}/#website`,
      },
    });
  }

  setProductSeo(product: Product) {
    const path = `/product/${product.no}`;
    const description =
      this.cleanText(product.descriptions?.join(' ')) ||
      `Shop ${product.name} at ${environment.appname}, a chic women's boutique style selected for confident everyday outfits.`;
    const image = product.images?.[0] || DEFAULT_IMAGE;
    const prices = product.priced_attr?.values
      ?.map((value) => value.price)
      .filter((price) => typeof price === 'number' && price > 0);
    const lowPrice = prices?.length ? Math.min(...prices) : undefined;
    const highPrice = prices?.length ? Math.max(...prices) : undefined;

    this.setSeo({
      title: product.name,
      description,
      path,
      image,
      type: 'product',
      keywords: `${product.name}, Pink Soul, women's fashion, boutique clothing`,
    });

    const productSchema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description,
      sku: product.no,
      url: this.absoluteUrl(path),
      image: product.images?.length
        ? product.images.map((item) => this.absoluteUrl(item))
        : [this.absoluteUrl(DEFAULT_IMAGE)],
      brand: {
        '@type': 'Brand',
        name: environment.appname,
      },
    };

    if (lowPrice !== undefined && highPrice !== undefined) {
      productSchema['offers'] = {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice,
        highPrice,
        availability: 'https://schema.org/InStock',
        url: this.absoluteUrl(path),
      };
    }

    this.setStructuredData('page-schema', productSchema);
  }

  private setPageStructuredData(title: string | undefined, description: string | undefined, path: string) {
    this.setStructuredData('page-schema', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title ? `${title} - ${environment.appname}` : this.title.getTitle(),
      description: description ?? SITE_DESCRIPTION,
      url: this.absoluteUrl(path),
      isPartOf: {
        '@id': `${environment.host}/#website`,
      },
    });
  }

  private setStructuredData(id: string, data: object) {
    let script = this.dom.getElementById(id) as HTMLScriptElement | null;
    if (!script) {
      script = this.dom.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      this.dom.head.appendChild(script);
    }
    script.text = JSON.stringify(data);
  }

  private clearStructuredData(id: string) {
    this.dom.getElementById(id)?.remove();
  }

  private cleanText(value: string | undefined): string {
    return (value ?? '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 300);
  }

  private absoluteUrl(path: string): string {
    if (path.startsWith('http')) {
      return path;
    }
    if (path.startsWith('/images')) {
      return `${environment.host}${path}`;
    }
    if (path.startsWith('/')) {
      return `${environment.host}${path}`;
    }
    return `${environment.api.s3endpoint}/${path}`;
  }
}
